import { useEffect, useState } from 'react'
import embeddingService, { ClusteringSourceNode } from '../services/embeddingService'

/** 클러스터링 대상 원천데이터 폴더 선택.
 *
 * 학습 데이터셋 생성과 같은 규칙이다 — **최하위 폴더만** 고를 수 있고, 상위 폴더는
 * 펼치기만 한다. 임베딩이 끝나지 않은 폴더는 체크박스를 잠그고 그 옆에 사유를 적는다.
 * 잠긴 이유를 화면에 쓰지 않으면 사용자는 기다려야 하는지 문제인지 알 수 없다.
 *
 * 마크업·색은 `RawPathPickerModal` / `CreateDatasetProjectModal` 관행을 따른다
 * (주요 버튼 bg-gray-800, 보조 버튼 border-gray-300, gray 계열).
 */
export default function ClusteringSourcePickerModal({
  isOpen, onClose, onSubmit,
}: {
  isOpen: boolean
  onClose: () => void
  onSubmit: (nodeIds: number[]) => Promise<void>
}) {
  const [nodes, setNodes] = useState<Record<number, ClusteringSourceNode>>({})
  const [childrenOf, setChildrenOf] = useState<Record<number, number[]>>({})
  const [roots, setRoots] = useState<number[]>([])
  const [expanded, setExpanded] = useState<Set<number>>(new Set())
  const [loadingIds, setLoadingIds] = useState<Set<number>>(new Set())
  const [selected, setSelected] = useState<Set<number>>(new Set())
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const absorb = (list: ClusteringSourceNode[]) => {
    setNodes(prev => {
      const next = { ...prev }
      list.forEach(n => { next[n.id] = n })
      return next
    })
  }

  useEffect(() => {
    if (!isOpen) return
    setNodes({}); setChildrenOf({}); setRoots([])
    setExpanded(new Set()); setSelected(new Set()); setError(null); setSubmitting(false)
    setLoading(true)
    ;(async () => {
      try {
        const list = await embeddingService.clusteringSources(0)
        absorb(list)
        setRoots(list.map(n => n.id))
      } catch (e: any) { setError(e?.message || '원천데이터 조회 실패') }
      finally { setLoading(false) }
    })()
  }, [isOpen])

  const toggleExpand = async (node: ClusteringSourceNode) => {
    const next = new Set(expanded)
    if (next.has(node.id)) {
      next.delete(node.id)
      setExpanded(next)
      return
    }
    next.add(node.id)
    setExpanded(next)
    if (childrenOf[node.id]) return
    setLoadingIds(prev => new Set(prev).add(node.id))
    try {
      const list = await embeddingService.clusteringSources(node.id)
      absorb(list)
      setChildrenOf(prev => ({ ...prev, [node.id]: list.map(n => n.id) }))
    } catch (e: any) { setError(e?.message || '하위 폴더 조회 실패') }
    finally {
      setLoadingIds(prev => { const s = new Set(prev); s.delete(node.id); return s })
    }
  }

  const toggleSelect = (nodeId: number) => {
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(nodeId)) next.delete(nodeId)
      else next.add(nodeId)
      return next
    })
  }

  const selectedNodes = Array.from(selected).map(id => nodes[id]).filter(Boolean)
  const selectedImages = selectedNodes.reduce((sum, n) => sum + n.embedded, 0)

  const handleSubmit = async () => {
    if (selected.size === 0 || submitting) return
    setSubmitting(true); setError(null)
    try {
      await onSubmit(Array.from(selected))
    } catch (e: any) {
      setError(e?.message || '클러스터링 생성 실패')
    } finally { setSubmitting(false) }
  }

  const renderNode = (id: number, level = 0) => {
    const node = nodes[id]
    if (!node) return null
    const isExpanded = expanded.has(id)
    const kids = childrenOf[id]
    return (
      <div key={id}>
        <div
          className="flex items-center py-1 px-2 hover:bg-gray-50"
          style={{ paddingLeft: `${level * 16 + 8}px` }}
        >
          {node.is_leaf ? (
            <input
              type="checkbox"
              className="w-4 h-4 mr-2 shrink-0 disabled:cursor-not-allowed"
              checked={selected.has(id)}
              disabled={!node.selectable || submitting}
              onChange={() => toggleSelect(id)}
            />
          ) : (
            <button
              type="button"
              onClick={() => toggleExpand(node)}
              className="mr-2 w-4 h-4 flex items-center justify-center shrink-0"
              aria-label={isExpanded ? '접기' : '펼치기'}
              title={isExpanded ? '접기' : '펼치기'}
            >
              <svg
                className={`w-4 h-4 text-gray-500 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                fill="none" stroke="currentColor" viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
              </svg>
            </button>
          )}
          <svg
            className="w-5 h-5 text-gray-700 mr-2 shrink-0" fill="none"
            stroke="currentColor" viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-5l-2-2H5a2 2 0 00-2 2z" />
          </svg>
          <span className={`text-sm truncate ${node.is_leaf && !node.selectable ? 'text-gray-400' : 'text-gray-800'}`}>
            {node.name}
          </span>
          {loadingIds.has(id) && <span className="ml-2 text-xs text-gray-400">불러오는 중...</span>}
          {node.is_leaf && (
            <span className="ml-auto flex shrink-0 items-center gap-2 text-xs">
              {node.selectable && (
                <span className="text-gray-500">임베딩 {node.embedded.toLocaleString()}장</span>
              )}
              {node.reason && (
                <span className={`px-2 py-0.5 rounded ${
                  node.selectable ? 'bg-amber-50 text-amber-700' : 'bg-gray-100 text-gray-500'
                }`}>{node.reason}</span>
              )}
            </span>
          )}
        </div>
        {isExpanded && kids && kids.map(kid => renderNode(kid, level + 1))}
      </div>
    )
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-3xl mx-4 shadow-lg">
        <div className="text-lg font-semibold mb-2">클러스터링 생성</div>
        <div className="text-xs text-gray-500 mb-4">
          대상 원천데이터 폴더를 고릅니다. 최하위 폴더만 선택할 수 있고, 임베딩이 끝나지 않은
          폴더는 사유와 함께 잠깁니다.
        </div>

        {error && <div className="mb-3 text-sm text-red-600">{error}</div>}

        {/* 높이를 고정한다. 내용에 맞춰 늘리면 폴더를 펼칠 때마다 모달이 커졌다 작아져
            방금 누른 항목이 눈에서 사라진다. 트리 칸만 스크롤한다. */}
        <div
          className="border border-gray-200 rounded overflow-y-auto"
          style={{ height: 480 }}
        >
          {loading ? (
            <div className="p-4 text-gray-500">로딩 중...</div>
          ) : roots.length === 0 ? (
            <div className="p-4 text-gray-500">원천 데이터가 없습니다.</div>
          ) : (
            roots.map(id => renderNode(id))
          )}
        </div>

        <div className="mt-4 flex items-center justify-between">
          <div className="text-xs text-gray-600">
            {selected.size === 0
              ? '선택된 폴더가 없습니다.'
              : `폴더 ${selected.size.toLocaleString()}개 · 이미지 ${selectedImages.toLocaleString()}장`}
          </div>
          <div className="flex items-center gap-2">
            <button
              className="px-3 py-1 text-sm border border-gray-300 rounded bg-white hover:bg-gray-50"
              onClick={onClose}
              disabled={submitting}
            >취소</button>
            <button
              className={`px-3 py-1 text-sm rounded text-white ${
                selected.size > 0 && !submitting ? 'bg-gray-800 hover:bg-gray-900' : 'bg-gray-400 cursor-not-allowed'
              }`}
              disabled={selected.size === 0 || submitting}
              onClick={handleSubmit}
            >
              {submitting ? '생성 중...' : '클러스터링 시작'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
